import { useState, useCallback, useRef } from 'react';
import type { Graph } from '@antv/x6';
import { getProceduralEdgeShape } from '../edges/procedural';
import { getStructuralEdgeShape } from '../edges/structural';
import { applyParallelEdgeOffset } from '../utils/edgeOffset';
import { layoutSiblingEdgeWithinParent, layoutChildToParentEdgeWithinParent, mergeSameTypeEdges, applySingleEdgeRouting } from '../utils/mergeSameTypeEdges';
import { applyInvocationZigzag } from '../utils/invocationZigzag';
import type { EdgeTypeChoice } from '../components/EdgeTypeDialog';
import type { EdgeDialogInfo } from '../types';

const STRUCTURAL_SUBTYPES = new Set([
  'aggregation-participation',
  'exhibition-characterization',
  'generalization-specialization',
  'classification-instantiation',
]);

function removeExistingStructuralEdges(
  graph: Graph,
  sourceId: string,
  targetId: string,
) {
  const edges = graph.getEdges();
  edges.forEach(edge => {
    if (edge.getSourceCellId?.() !== sourceId) return;
    if (edge.getTargetCellId?.() !== targetId) return;
    const data = edge.getData?.() || {};
    if (data.edgeCategory === 'structural') {
      edge.remove();
    }
  });
}

export function useEdgeDialog(
  graphRef: React.MutableRefObject<Graph | null>,
  options: {
    setStatusText: (text: string) => void;
    onStatusChange?: (status: string) => void;
  }
) {
  const { setStatusText, onStatusChange } = options;

  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogEdgeInfo, setDialogEdgeInfo] = useState<EdgeDialogInfo>({
    edge: null,
    sourceId: '',
    targetId: '',
    sourceLabel: '',
    targetLabel: '',
    sourceNodeType: 'object',
    sourcePort: undefined,
  });
  const pendingEdgeRef = useRef<any>(null);

  const showEdgeDialog = useCallback((info: EdgeDialogInfo) => {
    pendingEdgeRef.current = info.edge;
    setDialogEdgeInfo(info);
    setDialogVisible(true);
  }, []);

  const handleEdgeTypeConfirm = useCallback((choice: EdgeTypeChoice) => {
    const graph = graphRef.current;
    if (!graph || !pendingEdgeRef.current) {
      setDialogVisible(false);
      return;
    }

    const edge = pendingEdgeRef.current;
    const sourceId = dialogEdgeInfo.sourceId;
    const targetId = dialogEdgeInfo.targetId;

    if (!sourceId || !targetId) {
      edge.remove();
      pendingEdgeRef.current = null;
      setDialogVisible(false);
      setStatusText('连线取消');
      return;
    }

    // 移除临时连线
    edge.remove();

    // 检查是否已存在相同方向的连线（A→B 只能有一条，B→A 是独立的）
    const existingEdges = graph.getEdges().filter((e: any) => {
      const src = e.getSourceCellId?.();
      const tgt = e.getTargetCellId?.();
      return src === sourceId && tgt === targetId;
    });

    if (existingEdges.length > 0) {
      pendingEdgeRef.current = null;
      setDialogVisible(false);
      setStatusText('相同方向已存在连线，无法重复创建');
      onStatusChange?.('相同方向已存在连线，无法重复创建');
      return;
    }

    // 确定连线类型
    let shape: string;
    if (choice.category === 'structural') {
      shape = getStructuralEdgeShape(choice.subtype);
    } else {
      shape = getProceduralEdgeShape(choice.subtype);
    }

    // 结构性关系：同向同端点只保留一条边
    if (choice.category === 'structural') {
      removeExistingStructuralEdges(graph, sourceId, targetId);
    }

    const ensureConnectionPoint = (cfg: any) => {
      if (!cfg) return cfg;
      if (cfg.port === 'port-arrow') {
        cfg.connectionPoint = { name: 'anchor' };
      }
      return cfg;
    };

    const applyLineStyle = (edgeInstance: any, strokeWidth: number) => {
      if (!edgeInstance?.getAttrByPath) return;
      const lineAttrs = edgeInstance.getAttrByPath('line') || {};
      const marker = lineAttrs.targetMarker;
      edgeInstance.setAttrs({
        line: {
          ...lineAttrs,
          stroke: '#595959',
          strokeWidth,
          targetMarker: marker
            ? {
                ...marker,
                stroke: '#595959',
                fill: marker.fill ?? 'none',
              }
            : marker,
        },
      });
    };

    type EdgeAnchorName = 'top' | 'bottom' | 'left' | 'right';
    const anchorForPort = (port: string): EdgeAnchorName => {
      if (port === 'port-top') return 'top';
      if (port === 'port-bottom') return 'bottom';
      if (port === 'port-left') return 'left';
      return 'right';
    };

    const pickTargetPortConfig = (srcNode: any, tgtNode: any) => {
      const sBox = srcNode.getBBox();
      const tBox = tgtNode.getBBox();
      const sCx = sBox.x + sBox.width / 2;
      const sCy = sBox.y + sBox.height / 2;
      const tCx = tBox.x + tBox.width / 2;
      const tCy = tBox.y + tBox.height / 2;
      const dx = sCx - tCx;
      const dy = sCy - tCy;
      const port = Math.abs(dx) >= Math.abs(dy)
        ? (dx > 0 ? 'port-left' : 'port-right')
        : (dy > 0 ? 'port-top' : 'port-bottom');
      const anchorName = anchorForPort(port);
      return {
        port,
        anchor: { name: anchorName },
        connectionPoint: { name: 'anchor' },
      };
    };

    const targetNode = graph.getCellById(targetId) as any;
    const sourceNode = graph.getCellById(sourceId) as any;
    const isOptionTarget = targetNode?.getData?.()?.type === 'option';
    const isOptionArrowSource = dialogEdgeInfo.sourcePort === 'port-arrow';
    const targetConfig = isOptionTarget
      ? ensureConnectionPoint({ cell: targetId, port: 'port-arrow' })
      : isOptionArrowSource
        ? {
            cell: targetId,
            port: 'port-top',
            anchor: { name: 'top' },
            connectionPoint: { name: 'anchor' },
          }
        : { cell: targetId, ...pickTargetPortConfig(sourceNode, targetNode) };

    const sourceConfig: any = { cell: sourceId };
    if (dialogEdgeInfo.sourcePort) {
      sourceConfig.port = dialogEdgeInfo.sourcePort;
      ensureConnectionPoint(sourceConfig);
    }

    // 检查是否为同父节点内的子节点互连或子节点与父副本互连
    const srcParent = sourceNode?.getParent?.();
    const tgtParent = targetNode?.getParent?.();
    const isSiblingEdge = !!srcParent && !!tgtParent && srcParent.id === tgtParent.id;
    const srcData = sourceNode?.getData?.() || {};
    const tgtData = targetNode?.getData?.() || {};
    // 子节点 -> 父副本
    const isChildToParent = !!srcParent?.isNode?.() && !!tgtData.isParentCopy && srcParent.id === targetId;
    // 父副本 -> 子节点
    const isParentToChild = !!tgtParent?.isNode?.() && !!srcData.isParentCopy && tgtParent.id === sourceId;
    const isInParentEdge = isSiblingEdge || isChildToParent || isParentToChild;
    const siblingRouter = isInParentEdge
      ? { name: 'orth', args: { padding: 8 } }
      : undefined;
    const siblingParentNode = isSiblingEdge ? srcParent
      : isChildToParent ? srcParent
      : isParentToChild ? tgtParent
      : null;

    // 使用选定的类型创建新连线
    const postCreateAdjust = (edgeInstance: any) => {
      if (!edgeInstance?.isEdge?.()) return;
      if (dialogEdgeInfo.sourcePort === 'port-arrow') {
        applySingleEdgeRouting(graph, edgeInstance, 'top', isOptionTarget, true);
      }
    };

    const baseData = {
      edgeCategory: choice.category,
      edgeSubtype: choice.subtype,
      label: choice.label,
      ...(isOptionArrowSource ? { isOptionArrowEdge: true } : {}),
    };

    if (choice.subtype === 'agent-enablement') {
      // Agent 使能关系：实心圆点 + ¬C 标识
      const newEdge = graph.addEdge({
        shape,
        source: sourceConfig,
        target: targetConfig,
        router: siblingRouter,
        data: baseData,
      });
      applyLineStyle(newEdge, 2);
      postCreateAdjust(newEdge);

      // 动态添加 ¬C 标签到边上
      newEdge.appendLabel({
        position: 0.85,
        attrs: {
          label: {
            text: '¬C',
            fontSize: 10,
            fill: '#595959',
            fontWeight: 'bold',
          },
          rect: {
            fill: '#ffffff',
            stroke: '#ffffff',
            strokeWidth: 0,
          },
        },
      });
      if (siblingParentNode && siblingParentNode.isNode?.()) {
        if (isSiblingEdge) {
          layoutSiblingEdgeWithinParent(newEdge, sourceNode, targetNode, siblingParentNode as any);
        } else if (isChildToParent) {
          layoutChildToParentEdgeWithinParent(newEdge, sourceNode, targetNode);
        } else {
          layoutChildToParentEdgeWithinParent(newEdge, targetNode, sourceNode);
        }
      }
    } else {
      // 其他关系类型：显示关系名称
      const createdEdge = graph.addEdge({
        shape,
        source: sourceConfig,
        target: targetConfig,
        router: siblingRouter,
        data: baseData,
        attrs: {
          label: {
            text: choice.label,
            fill: '#595959',
            fontSize: 11,
          },
        },
      });
      applyLineStyle(createdEdge, 1.8);
      postCreateAdjust(createdEdge);
      if (siblingParentNode && siblingParentNode.isNode?.()) {
        if (isSiblingEdge) {
          layoutSiblingEdgeWithinParent(createdEdge, sourceNode, targetNode, siblingParentNode as any);
        } else if (isChildToParent) {
          layoutChildToParentEdgeWithinParent(createdEdge, sourceNode, targetNode);
        } else {
          layoutChildToParentEdgeWithinParent(createdEdge, targetNode, sourceNode);
        }
      }
    }

    // 对平行边施加偏移并重新做同类型合并，避免重叠
    applyParallelEdgeOffset(graph);
    mergeSameTypeEdges(graph);
    applyInvocationZigzag(graph);

    // 更新属性节点箭头可见性
    const updateOptionArrow = (node: any) => {
      if (!node || !node.hasPort?.('port-arrow')) return;
      const hasArrowEdge = graph.getConnectedEdges(node).some((e: any) => {
        const s = e.getSource?.();
        const t = e.getTarget?.();
        return (s?.port === 'port-arrow') || (t?.port === 'port-arrow');
      });
      const opacity = hasArrowEdge ? 0 : 1;
      node.setPortProp('port-arrow', 'attrs/arrowLine/opacity', opacity);
      node.setPortProp('port-arrow', 'attrs/arrowHead/opacity', opacity);
    };
    const srcNode = graph.getCellById(sourceId) as any;
    updateOptionArrow(srcNode);
    updateOptionArrow(targetNode);

    pendingEdgeRef.current = null;
    setDialogVisible(false);
    setStatusText(`创建连线: ${choice.label}`);
    onStatusChange?.(`创建连线: ${choice.label}`);
  }, [graphRef, dialogEdgeInfo.sourceId, dialogEdgeInfo.targetId, setStatusText, onStatusChange]);

  const handleEdgeTypeCancel = useCallback(() => {
    if (pendingEdgeRef.current) {
      pendingEdgeRef.current.remove();
      pendingEdgeRef.current = null;
    }
    setDialogVisible(false);
    setStatusText('已取消创建连线');
  }, [setStatusText]);

  return {
    dialogVisible,
    dialogEdgeInfo,
    showEdgeDialog,
    handleEdgeTypeConfirm,
    handleEdgeTypeCancel,
  };
}
